// src/PO-Process/POSummary.jsx

import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
} from "@mui/material";
import dayjs from "dayjs";
import api from "../axiosConfig";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Toaster } from "../components/Toaster";
import Card, { CardContent } from "./components/ui/card";
import { useNavigate } from "react-router-dom";
import ContainedButton from "../components/ui/ContainedButton";

// ------------------------------------------------------
// MULTISELECT RENDERER WITH "ALL" OPTION
// ------------------------------------------------------
const renderMultiSelect = ({
  label,
  options,
  selectedValues,
  onChange,
  placeholder,
}) => {
  const handleChange = (e) => {
    const value = e.target.value;

    // "All" logic
    if (value.includes("All")) {
      onChange([]);
      return;
    }

    onChange(value);
  };

  return (
    <Select
      multiple
      value={selectedValues}
      onChange={handleChange}
      renderValue={(selected) => {
        if (selected.length === 0) return placeholder;

        const visible = selected.slice(0, 2); // show only first 2
        const remaining = selected.length - visible.length;

        return (
          <>
            {visible.join(", ")}
            {remaining > 0 ? ` +${remaining}` : ""}
          </>
        );
      }}
      size="small"
      label={label}
    >
      <MenuItem value="All">
        <Checkbox checked={selectedValues.length === 0} />
        <ListItemText primary="All" />
      </MenuItem>

      {options.map((item) => (
        <MenuItem key={item} value={item}>
          <Checkbox checked={selectedValues.indexOf(item) > -1} />
          <ListItemText primary={item} />
        </MenuItem>
      ))}
    </Select>
  );
};

const POSummary = ({ open }) => {
  const navigate = useNavigate();

  const [poList, setPoList] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [groupBy, setGroupBy] = useState("monthly");

  const [cityList, setCityList] = useState([]);
  const [dcList, setDCList] = useState([]);
  const [pricelist, setPricelist] = useState([]);

  const [dcCityMap, setDcCityMap] = useState({});

  const [selectedCity, setSelectedCity] = useState([]);
  const [selectedDC, setSelectedDC] = useState([]);
  const [selectedPricelist, setSelectedPricelist] = useState([]);

  // ------------------------------------------------------
  // CITY LIST
  // ------------------------------------------------------
  useEffect(() => {
    const fetchCities = async () => {
      const res = await api.get("/get-city-list");
      setCityList(res.data || []);
    };
    fetchCities();
  }, []);

  // ------------------------------------------------------
  // DC LIST
  // ------------------------------------------------------
  useEffect(() => {
    const fetchDCNames = async () => {
      const res = await api.get("/get-dc-name-list");
      setDCList(res.data || []);
    };
    fetchDCNames();
  }, []);

  // ------------------------------------------------------
  // DC → CITY MAP
  // ------------------------------------------------------
  useEffect(() => {
    const fetchDcCityMap = async () => {
      const res = await api.get("/get-dc-city-map");
      setDcCityMap(res.data || {});
    };
    fetchDcCityMap();
  }, []);

  // ------------------------------------------------------
  // PO SUMMARY DATA
  // ------------------------------------------------------
  useEffect(() => {
    const fetchPOData = async () => {
      try {
        const res = await api.get("/po-home", {
          params: {
            start: "2020-01-01",
            end: dayjs().format("YYYY-MM-DD"),
          },
        });

        const data = res.data.files || [];

        // Map DC_Name → City
        const enrichedData = data.map((po) => {
          const citiesForDC = dcCityMap[po.DC_Name] || [];
          return {
            ...po,
            City: citiesForDC[0] || "", // if multiple cities per DC, pick first
          };
        });

        setPoList(enrichedData);

        const priceSet = new Set();
        enrichedData.forEach((po) => {
          if (po.Customer) priceSet.add(po.Customer);
        });
        setPricelist([...priceSet]);
      } catch (err) {
        console.error(err);
        Toaster.error("Failed to load PO data");
      }
    };

    fetchPOData();
  }, [dcCityMap]);

  // ------------------------------------------------------
  // FILTER + GROUP DATA
  // ------------------------------------------------------
  useEffect(() => {
    const filtered = poList.filter((po) => {

      const cityMatch =
        selectedCity.length === 0 || selectedCity.includes(po.City);

      const dcMatch =
        selectedDC.length === 0 || selectedDC.includes(po.DC_Name);

      const priceMatch =
        selectedPricelist.length === 0 ||
        selectedPricelist.includes(po.Customer);

      return cityMatch && dcMatch && priceMatch;
    });

    const grouped = {};

    filtered.forEach((po) => {
      const date = dayjs(po.Order_Date || po.createdAt || po.modifiedAt);
      if (!date.isValid()) return;

      let key = "";
      if (groupBy === "daily") key = date.format("YYYY-MM-DD");
      else if (groupBy === "weekly") key = date.startOf("week").format("YYYY-MM-DD");
      else key = date.startOf("month").format("YYYY-MM");

      if (!grouped[key]) {
        grouped[key] = {
          name:
            groupBy === "monthly"
              ? date.startOf("month").format("MMM YY")
              : key,
          processed: 0,
          downloaded: 0,
          yetToDownload: 0,
        };
      }

      grouped[key].processed++;
      if (po.status === "Downloaded" || po.status === "Posted to iDrive") grouped[key].downloaded++;
      else if (["Ready to Download", "Edits Pending"].includes(po.status))
        grouped[key].yetToDownload++;
    });

    const sorted = Object.entries(grouped)
      .sort((a, b) => new Date(a[0]) - new Date(b[0]))
      .map(([, v]) => v);

    setGraphData(sorted);
  }, [groupBy, poList, selectedCity, selectedDC, selectedPricelist]);

  return (
    <div
      className="ag-table-container"
      style={{
        marginLeft: open ? "240px" : "0px",
        transition: "margin-left 0.3s",
      }}
    >
      <Box sx={{ mb: 2, mt: 2 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Purchase Order Summary
            </Typography>

            {/* ------------------- TOP SUMMARY ------------------- */}
            <Box display="flex" gap={4} mb={3}>
              <Box>
                <Typography fontWeight="bold">Total Processed</Typography>
                <Typography color="#007bff">
                  {graphData.reduce((s, i) => s + i.processed, 0)}
                </Typography>
              </Box>

              <Box>
                <Typography fontWeight="bold">Total Downloads</Typography>
                <Typography color="#28a745">
                  {graphData.reduce((s, i) => s + i.downloaded, 0)}
                </Typography>
              </Box>
            </Box>

            {/* ------------------- FILTERS ------------------- */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <ToggleButtonGroup
                value={groupBy}
                exclusive
                onChange={(_, v) => v && setGroupBy(v)}
              >
                <ToggleButton value="daily">Daily</ToggleButton>
                <ToggleButton value="weekly">Weekly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
              </ToggleButtonGroup>

              {/* CITY */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>City</InputLabel>
                {renderMultiSelect({
                  label: "City",
                  options: cityList,
                  selectedValues: selectedCity,
                  onChange: (val) => {
                    setSelectedCity(val);

                    if (val.length === 0) return;

                    const autoDCs = Object.keys(dcCityMap).filter((dc) =>
                      (dcCityMap[dc] || []).some((c) => val.includes(c))
                    );
                    setSelectedDC((prev) => [...new Set([...prev, ...autoDCs])]);
                  },
                  placeholder: "All",
                })}
              </FormControl>

              {/* DC */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>DC Name</InputLabel>
                {renderMultiSelect({
                  label: "DC Name",
                  options: dcList,
                  selectedValues: selectedDC,
                  onChange: (val) => {
                    setSelectedDC(val);

                    if (val.length === 0) return;

                    const autoCities = val.flatMap((dc) => dcCityMap[dc] || []);
                    setSelectedCity((prev) => [...new Set([...prev, ...autoCities])]);
                  },
                  placeholder: "All",
                })}
              </FormControl>

              {/* PRICELIST */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Pricelist</InputLabel>
                {renderMultiSelect({
                  label: "Pricelist",
                  options: pricelist,
                  selectedValues: selectedPricelist,
                  onChange: setSelectedPricelist,
                  placeholder: "All",
                })}
              </FormControl>

              <ContainedButton onClick={() => navigate("/po-home")}>
                View All Purchase Orders
              </ContainedButton>

              <ContainedButton onClick={() => navigate("/po-report")}>
                View Report
              </ContainedButton>
            </Box>

            {/* ------------------- GRAPH ------------------- */}
            {graphData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={graphData} margin={{ top: 40, right: 30, left: 20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    tickMargin={10}
                  />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="processed" stroke="#007bff" strokeWidth={2} />
                  <Line type="monotone" dataKey="downloaded" stroke="#28a745" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography>No data available.</Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    </div>
  );
};

export default POSummary;
